import type {
  AssistantConsultantDeadlineOutcome,
  AssistantThread,
} from '@prospero/shared/src/assistant/assistant-contract';
import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import {
  consultantDeadlineQueryKeys,
  useConsultantUserId,
} from '../../hooks/consultant/use-consultant-deadlines';
import type { IAssistantService } from '../../services/assistant/assistant.service';
import { AssistantService } from '../../services/assistant/assistant.service';
import { assistantKeys } from './query-keys';

function withOutcomes(
  thread: AssistantThread,
  actionId: string,
  outcomes: AssistantConsultantDeadlineOutcome[],
): AssistantThread {
  return {
    ...thread,
    messages: thread.messages.map((message) => ({
      ...message,
      actions: message.actions.map((action) =>
        action.kind === 'CREATE_CONSULTANT_DEADLINES' && action.id === actionId
          ? { ...action, outcomes }
          : action,
      ),
    })),
  };
}

/** Every cached thread (any pageUrl scope) gets the outcomes: a reopened panel shows the card confirmed. */
function storeOutcomesInThreads(
  queryClient: QueryClient,
  actionId: string,
  outcomes: AssistantConsultantDeadlineOutcome[],
): void {
  queryClient.setQueriesData<AssistantThread>(
    { queryKey: [...assistantKeys.all, 'thread'] },
    (thread) => (thread ? withOutcomes(thread, actionId, outcomes) : thread),
  );
}

/**
 * "Modifica e crea" closed: the server may have stored the card outcomes (from the dialog or
 * from another tab), every cached thread reloads them (the dialog already refreshed the deadlines).
 * Resolves when the refetch is over, failed or not.
 */
export function useReloadAssistantThreads(): () => Promise<void> {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [...assistantKeys.all, 'thread'] });
}

/** Confirm of a deadline card: list, counters and calendar behind the panel refresh with it. */
export function useConfirmConsultantDeadlines() {
  const assistantService = useInjection<IAssistantService>(AssistantService);
  const queryClient = useQueryClient();
  const consultantUserId = useConsultantUserId();
  return useMutation({
    mutationFn: (actionId: string) => assistantService.confirmConsultantDeadlinesAction(actionId),
    onSuccess: (response, actionId) => {
      storeOutcomesInThreads(queryClient, actionId, response.outcomes);
      void queryClient.invalidateQueries({
        queryKey: consultantDeadlineQueryKeys.all(consultantUserId),
      });
    },
  });
}
